// Editing one button's settings — the write half of buttonStatus.js.
//
// Ported from SmartLifeIndia's buttonSettingsFunctions.dart and the older
// smartify-automation button_editing_form.dart, which agree on the encoding.
//
// ⚠️ SAVING IS TWO STEPS, AND SKIPPING THE SECOND LOSES THE SETTING.
//
//   1. write the columns    UpdateOutputDetailsByUniqueNumberAndOutput
//   2. push the registers   MQTT `swritewrite…` to the device
//
// The database row is what the app reads back; the REGISTERS are what the
// switch on the wall actually obeys. Write only the row and the app shows the
// new setting while the hardware carries on with the old one — which is worse
// than not saving at all, because it looks like it worked.
//
// Button State is the one exception: the backend's power-on jobs act on
// PowerOnAction directly, so there is nothing to push for it.

import { post, getOutputs } from '../api/api';
import { rowForOutput } from './buttonStatus';
import { publishCommand } from '../utils/deviceController';

// ── Option lists — the INDEX is the stored value ───────────────────────────
export const ALL_ON_OPTIONS = ['Action On', 'No Action', 'Schedule Time', 'Reverse'];
export const ALL_OFF_OPTIONS = ['Action Off', 'No Action', 'Schedule Time', 'Reverse'];
export const BUTTON_STATE_OPTIONS = [
  'Remember Last State',
  'Do Not Remember Last State',
  'Restart After One Minute (For Router)',
];

// "Schedule Time" is the only choice that carries a delay in minutes.
export const SCHEDULE_TIME_OPTION = 2;

// ── The packed column ──────────────────────────────────────────────────────
// on_all_on_status = (allOn * 10) + allOff
//
// ⚠️ Read the tens digit for All ON and the units for All OFF. A value of 1 is
// therefore "Action On / No Action", NOT "takes part in All On".
export const packAllOnOff = (allOn, allOff) =>
  (Number(allOn) || 0) * 10 + (Number(allOff) || 0);

export function unpackAllOnOff(status) {
  const s = Number(status) || 0;
  const clamp = (n) => Math.min(3, Math.max(0, n));
  return { allOn: clamp(Math.floor(s / 10)), allOff: clamp(s % 10) };
}

/**
 * The firmware register write.
 *
 * Registers are addressed as base + output number (1-based):
 *   32 + o   OnTimeAfterAllOff   minutes, for "Schedule Time" on All Off
 *   48 + o   the packed <allOn><allOff> pair
 *   64 + o   OnTimeAfterAllOn    minutes, for "Schedule Time" on All On
 *   80 + o   OnTimeAfterOn       key-press-off-after, minutes
 *
 * All four go every time, rebuilt from the merged row, because the firmware
 * takes them as one message and a partial write would leave the others stale.
 */
export function buildRegisterPush(output, row) {
  const o = Number(output);
  const int = (v) => Number(v) || 0;
  const { allOn, allOff } = unpackAllOnOff(row.on_all_on_status);
  return `swritewrite${64 + o}-${int(row.OnTimeAfterAllOn)}`
    + `,${32 + o}-${int(row.OnTimeAfterAllOff)}`
    + `,${48 + o}-${allOn}${allOff}`
    + `,${80 + o}-${int(row.OnTimeAfterOn)}`;
}

/**
 * The current row for ONE output.
 *
 * ⚠️ THERE IS NO PER-OUTPUT GET. An earlier version called
 * `GetOutputDetailbyUniqueNumberandOutput`, which does not exist — it 404s, and
 * the dialog reported "could not load this button's settings" for every button
 * on every device. The device-wide endpoint is the only one, so the row is
 * picked out of it here.
 *
 * Matched on the Output NUMBER, not array position: the rows come back in
 * whatever order the query gives, and position-matching silently returns a
 * different button's settings.
 */
export async function getOutputRow(uniqueNumber, output) {
  const res = await getOutputs(uniqueNumber);
  const rows = Array.isArray(res?.data) ? res.data : [];
  const row = rowForOutput(rows, output);
  // Same { data: [...] } shape the caller already expects from a REST helper.
  return { data: row ? [row] : [] };
}

/** Write columns on OutputDetails for one output. */
export const updateOutputDetails = (uniqueNumber, output, data) =>
  post('UpdateOutputDetailsByUniqueNumberAndOutput', {
    data,
    UniqueNumber: uniqueNumber,
    Output: output,
  });

/**
 * Save changes and push the registers.
 *
 * `currentRow` is merged with `changes` LOCALLY rather than re-read, because a
 * re-read races the write — the Dart hit the same problem and solved it the
 * same way.
 *
 * -> { ok, error }
 */
export async function saveButtonSettings(uniqueNumber, output, changes, currentRow, userId) {
  try {
    await updateOutputDetails(uniqueNumber, output, changes);
  } catch (e) {
    return { ok: false, error: 'Could not save. Check your connection and try again.' };
  }

  // Best-effort: the row is saved either way, and a device that is offline will
  // pick the values up from the server. Failing the whole save because the push
  // did not go out would misreport what actually happened.
  try {
    const merged = { ...(currentRow || {}), ...changes };
    publishCommand(uniqueNumber, buildRegisterPush(output, merged), userId);
  } catch (e) { /* ignore */ }

  return { ok: true };
}
